import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Settings2, Loader2, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const trig = "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm";

export const PlatformsPicker = ({ value = [], options, onOptions, onChange }) => {
  const [name, setName] = useState(""), [busy, setBusy] = useState(false);
  const toggle = (p) => onChange(value.includes(p) ? value.filter((x) => x !== p) : [...value, p]);
  const add = async () => {
    const n = name.trim(); if (!n) return;
    setBusy(true);
    try { const r = await api.addRidersPlatform(n); onOptions(r.platforms || [...options, n]); if (!value.includes(n)) onChange([...value, n]); setName(""); toast.success(`أُضيف تطبيق «${n}»`); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); } finally { setBusy(false); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={trig} data-testid="emp-platform-select"><span className="truncate">{value.length ? value.join("، ") : "جميع التطبيقات"}</span></button>
      </PopoverTrigger>
      <PopoverContent dir="rtl" align="start" className="w-64 p-2" data-testid="emp-platform-popover">
        <button type="button" onClick={() => onChange([])} className={`mb-1 flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-slate-50 ${!value.length ? "font-bold text-blue-700" : ""}`} data-testid="emp-platform-all">
          جميع التطبيقات {!value.length && <Check className="h-4 w-4" />}
        </button>
        <div className="max-h-56 overflow-y-auto">
          {[...new Set([...options, ...value])].map((p) => (
            <label key={p} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`emp-platform-opt-${p}`}>
              <input type="checkbox" checked={value.includes(p)} onChange={() => toggle(p)} /> {p}
            </label>
          ))}
        </div>
        <div className="mt-2 flex gap-1 border-t pt-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="تطبيق جديد" className="h-8 text-xs" data-testid="emp-platform-new" />
          <Button type="button" size="sm" className="h-8" disabled={busy || !name.trim()} onClick={add} data-testid="emp-platform-add">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TypesEditor = ({ types, meta = {}, onSaved }) => {
  const [rows, setRows] = useState([]), [busy, setBusy] = useState(false);
  useEffect(() => { setRows(types.map((t) => ({ old: t, name: t, invoice: !!meta[t]?.invoice, company_pct: meta[t]?.company_pct ?? 0, share_mode: meta[t]?.share_mode || "pct" }))); }, [types, meta]);
  const upd = (i, k, v) => setRows(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setBusy(true);
    try {
      const renames = Object.fromEntries(rows.filter((r) => r.old && r.name.trim() && r.old !== r.name.trim()).map((r) => [r.old, r.name.trim()]));
      const m = Object.fromEntries(rows.filter((x) => x.name.trim()).map((x) => [x.name.trim(), { invoice: x.invoice, company_pct: Number(x.company_pct) || 0, share_mode: x.share_mode || "pct" }]));
      const r = await api.saveContractTypes(rows.map((x) => x.name), renames, m);
      onSaved(r.types, r.meta); toast.success("تم حفظ أنواع العقود");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid="emp-contract-editor">
      {rows.map((r, i) => (
        <div key={i} className="space-y-1 rounded-lg border border-slate-100 p-1.5">
          <div className="flex gap-1">
            <Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} className="h-8 text-xs" data-testid={`emp-contract-type-${i}`} />
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`emp-contract-type-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <label className="flex items-center gap-1"><input type="checkbox" checked={r.invoice} onChange={(e) => upd(i, "invoice", e.target.checked)} data-testid={`emp-contract-invoice-${i}`} /> يصدر فاتورة</label>
            {r.invoice && <label className="flex items-center gap-1">حصة الشركة
              <select value={r.share_mode || "pct"} onChange={(e) => upd(i, "share_mode", e.target.value)} className="h-7 rounded-md border border-slate-200 bg-white px-1 text-xs" data-testid={`emp-contract-mode-${i}`}>
                <option value="pct">نسبة %</option><option value="per_order">مبلغ لكل طلب</option><option value="fixed">مبلغ شهري ثابت</option>
              </select>
              <Input type="number" step="0.01" value={r.company_pct} onChange={(e) => upd(i, "company_pct", e.target.value)} className="h-7 w-16 text-xs" data-testid={`emp-contract-pct-${i}`} />
              <span className="text-[10px] text-slate-500">{(r.share_mode || "pct") === "pct" ? "%" : "ر.س"}</span></label>}
          </div>
        </div>
      ))}
      <div className="flex gap-1">
        <Button type="button" size="sm" variant="outline" className="h-8 flex-1" onClick={() => setRows([...rows, { old: "", name: "", invoice: false, company_pct: 0 }])} data-testid="emp-contract-type-add"><Plus className="h-4 w-4" /> نوع جديد</Button>
        <Button type="button" size="sm" className="h-8 flex-1" disabled={busy} onClick={save} data-testid="emp-contract-type-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}</Button>
      </div>
      <p className="text-[10px] text-slate-400">تعديل الاسم يُحدّث كل الموظفين على النوع القديم. «يصدر فاتورة»: يُحسب له إجمالي الإيراد ناقص حصة الشركة (نسبة، أو مبلغ × عدد الطلبات، أو مبلغ شهري ثابت) بدل الراتب، وتصدر له فاتورة مستحقات بدل المسيّر.</p>
    </div>
  );
};

export const ContractTypeField = ({ value, onChange }) => {
  const [types, setTypes] = useState([]), [meta, setMeta] = useState({}), [edit, setEdit] = useState(false);
  useEffect(() => { api.contractTypes().then((r) => { setTypes(r.types || []); setMeta(r.meta || {}); }).catch(() => {}); }, []);
  return (
    <div className="flex items-center gap-1.5">
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 flex-1 rounded-md border border-input bg-background px-2 text-sm" data-testid="emp-contract-type-select">
        <option value="">— غير محدد —</option>
        {[...new Set([...types, value].filter(Boolean))].map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <Popover open={edit} onOpenChange={setEdit}>
        <PopoverTrigger asChild>
          <Button type="button" size="icon" variant="outline" className="h-10 w-10 shrink-0" title="إضافة / تعديل أنواع العقود" data-testid="emp-contract-type-manage"><Settings2 className="h-4 w-4" /></Button>
        </PopoverTrigger>
        <PopoverContent dir="rtl" align="end" className="w-72 p-2"><TypesEditor types={types} meta={meta} onSaved={(t, m) => { setTypes(t); setMeta(m || {}); setEdit(false); }} /></PopoverContent>
      </Popover>
    </div>
  );
};

export const BulkContractType = ({ selected, onDone }) => {
  const [types, setTypes] = useState([]), [v, setV] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { api.contractTypes().then((r) => setTypes(r.types || [])).catch(() => {}); }, []);
  if (!selected?.size) return null;
  const apply = async () => {
    setBusy(true);
    try { const r = await api.bulkContractType([...selected], v); toast.success(`تم تعيين نوع العقد لـ ${r.updated} موظف`); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التعيين"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1" data-testid="bulk-contract-type">
      <select value={v} onChange={(e) => setV(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="bulk-contract-type-select">
        <option value="">نوع العقد: غير محدد</option>
        {types.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <Button type="button" size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={apply} data-testid="bulk-contract-type-apply">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تعيين نوع العقد"}
      </Button>
    </div>
  );
};

export const BulkPlatform = ({ selected, onDone }) => {
  const [plats, setPlats] = useState([]), [v, setV] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { api.ridersConfig().then((r) => setPlats(r?.platforms || [])).catch(() => {}); }, []);
  if (!selected?.size) return null;
  const apply = async () => {
    if (!v) return toast.error("اختر التطبيق");
    setBusy(true);
    try { const r = await api.bulkPlatform([...selected], v); toast.success(`تم تعيين التطبيق لـ ${r.updated} موظف`); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التعيين"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1" data-testid="bulk-platform">
      <select value={v} onChange={(e) => setV(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="bulk-platform-select">
        <option value="">اختر تطبيق المندوب</option>
        {plats.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <Button type="button" size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={apply} data-testid="bulk-platform-apply">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تعيين التطبيق"}
      </Button>
    </div>
  );
};
